import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

// 再利用可能な矢印アイコンコンポーネント
const ArrowIcon: React.FC<{ d: string }> = ({ d }) => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
}) => {
  // ページ番号の配列を生成
  const getPageNumbers = (): (number | string)[] => {
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    return getPageNumbersWithEllipsis();
  };

  const getPageNumbersWithEllipsis = (): (number | string)[] => {
    const pages: (number | string)[] = [];

    if (currentPage <= 4) {
      return getEarlyPages(pages);
    } else if (currentPage >= totalPages - 3) {
      return getLatePages(pages);
    } else {
      return getMiddlePages(pages);
    }
  };

  const ELLIPSIS = '...';
  const BUTTON_STYLE = 'text-gray-700 hover:text-green-600 hover:bg-green-50';

  const getEarlyPages = (pages: (number | string)[]): (number | string)[] => {
    for (let i = 1; i <= 5; i++) {
      pages.push(i);
    }
    pages.push(ELLIPSIS);
    pages.push(totalPages);
    return pages;
  };

  const getLatePages = (pages: (number | string)[]): (number | string)[] => {
    pages.push(1);
    pages.push(ELLIPSIS);
    for (let i = totalPages - 4; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getMiddlePages = (pages: (number | string)[]): (number | string)[] => {
    pages.push(1);
    pages.push(ELLIPSIS);
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push(ELLIPSIS);
    pages.push(totalPages);
    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className={`flex justify-center items-center space-x-2 ${className}`}
      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
      aria-label="ページネーション"
    >
      {/* 前のページボタン */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center justify-center w-10 h-10 rounded-md text-sm font-medium transition-colors ${
          currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : BUTTON_STYLE
        }`}
        aria-label="前のページ"
      >
        <ArrowIcon d="M15 19l-7-7 7-7" />
      </button>

      {/* ページ番号 */}
      {pageNumbers.map((page, index) => (
        <React.Fragment key={index}>
          {page === ELLIPSIS ? (
            <span className="px-3 py-2 text-gray-500">{ELLIPSIS}</span>
          ) : (
            <button
              onClick={() => onPageChange(page as number)}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                currentPage === page ? 'bg-green-600 text-white' : BUTTON_STYLE
              }`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          )}
        </React.Fragment>
      ))}

      {/* 次のページボタン */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center justify-center w-10 h-10 rounded-md text-sm font-medium transition-colors ${
          currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : BUTTON_STYLE
        }`}
        aria-label="次のページ"
      >
        <ArrowIcon d="M9 5l7 7-7 7" />
      </button>
    </nav>
  );
};

export default Pagination;
